import axios from 'axios';
import {defineStore} from 'pinia';
import {usePage} from '@inertiajs/vue3';
import {nextTick} from 'vue';
import {numberCommaFormat, checkDocument, setLocalStorage} from '@js/helpers.ts';

const orderTypes = {
    default: {type: 'default', title: 'По популярности'},
    price_asc: {type: 'price_asc', title: 'Сначала дешевле'},
    price_desc: {type: 'price_desc', title: 'Сначала дороже'},
};

const perPageArray = [15, 30, 50, 100];

const defaultFilterData = {
    price: [0, 0],
    per_page: 15,
    sort: orderTypes.default.type,
};

const CATALOG_SEGMENT = 'catalog';

const gridMods = {
    wide: 'wide',
    small: 'small',
};

export const useCatalogStore = defineStore('catalogStore', {
    state: () => ({
        grid_mods: gridMods,
        grid_mode: gridMods.wide,

        type_slug: null,
        is_filter_open: false,
        is_filter_watcher_on: false,

        loadings: {
            filter_first_load: false,
            catalog_loaded: false,
            filter_limits_loaded: false,
        },

        total_result: 0,
        order_types: orderTypes,
        per_page_array: perPageArray,

        filter_data: {
            limits: {
                min_price: 0,
                max_price: 0,
                price: [0, 0],
            },
            types: [],
            categories: [],
            sub_categories: [],
            characteristics: [],
            price_types: [],
            specification_values: {},
        },

        filter: JSON.parse(JSON.stringify(defaultFilterData)),

        selected_types: [],
        selected_categories: [],
        selected_sub_categories: [],
        selected_characteristics: {},
        selected_price_types: [],
        selected_specification_values: [],

        pagination: {
            current_page: 1,
            total_pages: 1,
        },

        timeout_events: {
            filter: undefined,
        },

        products: [],
        selected_filters: [],
        search_query: null,
        savedScroll: 0,
        popstateBound: false,
    }),

    actions: {
        readTypeSlugFromPath() {
            let pathname = '';
            try {
                const page = usePage();
                pathname = (page?.url?.split('?')[0]) || '/';
            } catch {
                pathname = window.location.pathname || '/';
            }

            const parts = pathname.split('/').filter(Boolean);
            const idx = parts.indexOf(CATALOG_SEGMENT);
            const raw = (idx !== -1 && parts[idx + 1]) ? parts[idx + 1] : null;

            return raw ? decodeURIComponent(raw) : null;
        },

        buildCatalogPath() {
            const typeSlug = this.type_slug ? encodeURIComponent(this.type_slug) : null;
            return `/${CATALOG_SEGMENT}${typeSlug ? `/${typeSlug}` : ''}`;
        },

        toggleFilter() {
            this.is_filter_open = !this.is_filter_open;
        },

        setSavedScroll(y) {
            this.savedScroll = y;
        },

        onPageChange(page) {
            this.pagination.current_page = page;
            this.getCatalog();
            this.addParamsToUrl();

            if (checkDocument()) {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }
        },

        changeOrder(type) {
            this.filter.sort = type;
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        changePerPage(perPage) {
            this.filter.per_page = perPage;
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        hasUrlParams() {
            try {
                const page = usePage();
                const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
                return queryParams.toString().length > 0;
            } catch {
                const queryParams = new URLSearchParams(window.location.search || '');
                return queryParams.toString().length > 0;
            }
        },

        syncUrlParamsWithState() {
            this.type_slug = this.readTypeSlugFromPath();

            let queryParams;
            try {
                const page = usePage();
                queryParams = new URLSearchParams(page.url.split('?')[1] || '');
            } catch {
                queryParams = new URLSearchParams(window.location.search || '');
            }

            const getJson = (key, fallback) => {
                try {
                    return queryParams.has(key) ? JSON.parse(queryParams.get(key)) : fallback;
                } catch {
                    return fallback;
                }
            };

            this.selected_types = getJson('types', []);
            this.selected_categories = getJson('categories', []);
            this.selected_sub_categories = getJson('sub_categories', []);
            this.selected_price_types = getJson('price_type_codes', []);
            this.selected_specification_values = getJson('specification_values_flat', []);

            const chFromUrl = getJson('characteristics', []);
            if (Array.isArray(chFromUrl)) {
                const obj = {};
                chFromUrl.forEach((c) => {
                    if (c && typeof c.type_id !== 'undefined' && Array.isArray(c.values)) {
                        obj[c.type_id] = c.values;
                    }
                });
                this.selected_characteristics = obj;
            } else {
                this.selected_characteristics = {};
            }

            const per = queryParams.has('per_page')
                ? Number(queryParams.get('per_page'))
                : defaultFilterData.per_page;
            this.filter.per_page = perPageArray.includes(per) ? per : defaultFilterData.per_page;

            this.filter.sort = queryParams.has('sort')
                ? queryParams.get('sort')
                : defaultFilterData.sort;

            this.search_query = queryParams.has('search')
                ? queryParams.get('search')
                : null;

            this.pagination.current_page = queryParams.has('page')
                ? parseInt(queryParams.get('page'))
                : 1;

            const pf = queryParams.has('price_from') ? Number(queryParams.get('price_from')) : null;
            const pt = queryParams.has('price_to') ? Number(queryParams.get('price_to')) : null;

            if (pf !== null || pt !== null) {
                const cur = [...this.filter.price];
                if (pf !== null && !Number.isNaN(pf)) cur[0] = pf;
                if (pt !== null && !Number.isNaN(pt)) cur[1] = pt;
                this.filter.price = cur;
            }
        },

        addParamsToUrl() {
            if (!history.replaceState) return;

            const params = new URLSearchParams();

            if (this.search_query) params.set('search', this.search_query);

            if (Array.isArray(this.filter.price)) {
                params.set('price_from', this.filter.price?.[0] ?? '');
                params.set('price_to', this.filter.price?.[1] ?? '');
            }

            if (this.selected_types?.length) {
                params.set('types', JSON.stringify(this.selected_types));
            }

            if (this.selected_categories?.length) {
                params.set('categories', JSON.stringify(this.selected_categories));
            }

            if (this.selected_sub_categories?.length) {
                params.set('sub_categories', JSON.stringify(this.selected_sub_categories));
            }

            if (this.selected_price_types?.length) {
                params.set('price_type_codes', JSON.stringify(this.selected_price_types));
            }

            if (this.selected_specification_values?.length) {
                params.set('specification_values_flat', JSON.stringify(this.selected_specification_values));
            }

            const chPayload = this.buildCharacteristicsPayload();
            if (chPayload.length) {
                params.set('characteristics', JSON.stringify(chPayload));
            }

            params.set('sort', this.filter.sort);
            params.set('page', this.pagination.current_page);
            params.set('per_page', this.filter.per_page);

            for (const [k, v] of [...params.entries()]) {
                if (v === '' || v === 'null' || v === '[]') params.delete(k);
            }

            const basePath = this.buildCatalogPath();
            const qs = params.toString();
            const newurl = `${window.location.protocol}//${window.location.host}${basePath}${qs ? `?${qs}` : ''}`;

            window.history.replaceState({path: newurl}, '', newurl);
        },

        getParamFromUrl(param, defaultValue) {
            try {
                const page = usePage();
                const queryParams = new URLSearchParams(page.url.split('?')[1] || '');
                return queryParams.has(param) ? parseFloat(queryParams.get(param)) : defaultValue;
            } catch {
                const queryParams = new URLSearchParams(window.location.search || '');
                return queryParams.has(param) ? parseFloat(queryParams.get(param)) : defaultValue;
            }
        },

        buildCharacteristicsPayload() {
            const out = [];

            Object.entries(this.selected_characteristics || {}).forEach(([typeId, values]) => {
                if (Array.isArray(values) && values.length) {
                    out.push({
                        type_id: Number(typeId),
                        values,
                    });
                }
            });

            return out;
        },

        checkRangeBeforeSend(type, checkType) {
            const idx = checkType === 'max' ? 1 : 0;
            const limit = this.filter_data.limits?.[type]?.[idx];
            const val = this.filter?.[type]?.[idx];

            if (typeof limit === 'undefined' || typeof val === 'undefined') return null;

            return Number(limit) !== Number(val) ? val : null;
        },

        buildCatalogParams() {
            return {
                type: this.type_slug || null,
                search: this.search_query || null,

                price_from: this.checkRangeBeforeSend('price', 'min'),
                price_to: this.checkRangeBeforeSend('price', 'max'),

                types: this.selected_types?.length ? this.selected_types : null,
                categories: this.selected_categories?.length ? this.selected_categories : null,
                sub_categories: this.selected_sub_categories?.length ? this.selected_sub_categories : null,

                price_type_codes: this.selected_price_types?.length ? this.selected_price_types : null,
                specification_values_flat: this.selected_specification_values?.length
                    ? this.selected_specification_values
                    : null,

                characteristics: this.buildCharacteristicsPayload(),

                sort: this.filter.sort,
                page: this.pagination.current_page,
                per_page: this.filter.per_page,
            };
        },

        async getFilterData() {
            this.loadings.catalog_loaded = false;

            try {
                const {data} = await axios.get('/axios/catalog/get-filter-data', {
                    params: this.buildCatalogParams(),
                });

                const minP = Number(data?.limits?.min_price ?? 0);
                const maxP = Number(data?.limits?.max_price ?? 0);

                this.filter_data.limits.min_price = minP;
                this.filter_data.limits.max_price = maxP;
                this.filter_data.limits.price = [minP, maxP];

                if (!this.loadings.filter_limits_loaded) {
                    const pf = this.getParamFromUrl('price_from', null);
                    const pt = this.getParamFromUrl('price_to', null);

                    this.filter.price = [
                        pf !== null && !Number.isNaN(pf) ? pf : minP,
                        pt !== null && !Number.isNaN(pt) ? pt : maxP,
                    ];
                }

                this.filter_data.types = data?.filter_data?.types ?? [];
                this.filter_data.categories = data?.filter_data?.categories ?? [];
                this.filter_data.sub_categories = data?.filter_data?.sub_categories ?? [];
                this.filter_data.characteristics = data?.filter_data?.characteristics ?? [];
                this.filter_data.price_types = data?.filter_data?.price_types ?? [];
                this.filter_data.specification_values = data?.filter_data?.specification_values ?? {};

                this.loadings.filter_limits_loaded = true;
                this.loadings.filter_first_load = true;

                this.updateSelectedFilters();
                this.addParamsToUrl();
            } catch (e) {
                console.error('getFilterData error:', e?.message || e);
                this.loadings.filter_limits_loaded = false;
            }
        },

        async getCatalog() {
            try {
                const {data} = await axios.get('/axios/catalog', {
                    params: this.buildCatalogParams(),
                });

                this.pagination.total_pages = data?.total_pages ?? 1;
                this.total_result = data?.total ?? 0;
                this.products = data?.products ?? [];
                this.loadings.catalog_loaded = true;

                this.updateSelectedFilters();
                this.addParamsToUrl();
            } catch (e) {
                console.error('getCatalog error:', e?.message || e);
                this.loadings.catalog_loaded = false;
            }
        },

        toggleType(slug) {
            if (!slug) return;

            const set = new Set(this.selected_types);
            set.has(slug) ? set.delete(slug) : set.add(slug);

            this.selected_types = Array.from(set);
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        toggleCategory(slug) {
            if (!slug) return;

            const set = new Set(this.selected_categories);
            set.has(slug) ? set.delete(slug) : set.add(slug);

            this.selected_categories = Array.from(set);
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        toggleSubCategory(slug) {
            if (!slug) return;

            const set = new Set(this.selected_sub_categories);
            set.has(slug) ? set.delete(slug) : set.add(slug);

            this.selected_sub_categories = Array.from(set);
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        togglePriceType(code) {
            if (!code) return;

            const set = new Set(this.selected_price_types);
            set.has(code) ? set.delete(code) : set.add(code);

            this.selected_price_types = Array.from(set);
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        toggleSpecificationValue(flat) {
            if (!flat) return;

            const set = new Set(this.selected_specification_values);
            set.has(flat) ? set.delete(flat) : set.add(flat);

            this.selected_specification_values = Array.from(set);
            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        isChecked(typeId, value) {
            const arr = this.selected_characteristics?.[typeId] || [];
            return arr.includes(value);
        },

        toggleCharacteristic(typeId, value) {
            if (typeof typeId === 'undefined' || value === undefined || value === null) return;

            const current = this.selected_characteristics?.[typeId]
                ? [...this.selected_characteristics[typeId]]
                : [];

            const idx = current.indexOf(value);
            if (idx === -1) current.push(value);
            else current.splice(idx, 1);

            this.selected_characteristics = {
                ...this.selected_characteristics,
                [typeId]: current,
            };

            this.pagination.current_page = 1;
            this.applyFiltersDebounced();
        },

        applyFiltersDebounced(delay = 350) {
            if (this.timeout_events.filter) clearTimeout(this.timeout_events.filter);

            this.timeout_events.filter = setTimeout(async () => {
                await this.getFilterData();
                await nextTick();
                await this.getCatalog();
            }, delay);
        },

        updateSelectedFilters() {
            const selected = [];

            (this.selected_types || []).forEach((slug) => {
                const item = (this.filter_data.types || []).find(x => x.slug === slug);
                selected.push({
                    label: 'Тип',
                    title: item?.title || slug,
                    value: slug,
                    field: 'types',
                });
            });

            (this.selected_categories || []).forEach((slug) => {
                const item = (this.filter_data.categories || []).find(x => x.slug === slug);
                selected.push({
                    label: 'Категория',
                    title: item?.title || slug,
                    value: slug,
                    field: 'categories',
                });
            });

            (this.selected_sub_categories || []).forEach((slug) => {
                const item = (this.filter_data.sub_categories || []).find(x => x.slug === slug);
                selected.push({
                    label: 'Подкатегория',
                    title: item?.title || slug,
                    value: slug,
                    field: 'sub_categories',
                });
            });

            const [min, max] = this.filter.price || [0, 0];
            const [limitMin, limitMax] = this.filter_data.limits?.price || [0, 0];

            if (min > limitMin || max < limitMax) {
                selected.push({
                    label: 'Цена',
                    title: `${numberCommaFormat(min)} - ${numberCommaFormat(max)} ₽`,
                    value: `${min}_${max}`,
                    field: 'price',
                });
            }

            (this.selected_price_types || []).forEach((code) => {
                const item = (this.filter_data.price_types || []).find(x => x.code === code);
                selected.push({
                    label: 'Тип цены',
                    title: item?.title || code,
                    value: code,
                    field: 'price_types',
                });
            });

            (this.selected_specification_values || []).forEach((flat) => {
                let title = flat;

                const groups = this.filter_data.specification_values || {};
                for (const [groupTitle, values] of Object.entries(groups)) {
                    const found = (values || []).find(v => v.flat === flat);
                    if (found) {
                        title = `${groupTitle}: ${found.value}`;
                        break;
                    }
                }

                selected.push({
                    label: 'Спецификация',
                    title,
                    value: flat,
                    field: 'specification_values',
                });
            });

            const typesById = {};
            (this.filter_data.characteristics || []).forEach((group) => {
                typesById[group.type_id] = group;
            });

            Object.entries(this.selected_characteristics || {}).forEach(([typeId, values]) => {
                const group = typesById[typeId];
                const label = group?.type_name || `Характеристика ${typeId}`;

                (values || []).forEach((val) => {
                    selected.push({
                        label,
                        title: val,
                        value: val,
                        field: 'characteristics',
                        type_id: Number(typeId),
                    });
                });
            });

            this.selected_filters = selected;
        },

        removeFilter(tag) {
            if (!tag?.field) return;

            if (tag.field === 'price') {
                const [limitMin, limitMax] = this.filter_data.limits?.price || [0, 0];
                this.filter.price = [limitMin, limitMax];
            } else if (tag.field === 'types') {
                this.selected_types = (this.selected_types || []).filter(v => v !== tag.value);
            } else if (tag.field === 'categories') {
                this.selected_categories = (this.selected_categories || []).filter(v => v !== tag.value);
            } else if (tag.field === 'sub_categories') {
                this.selected_sub_categories = (this.selected_sub_categories || []).filter(v => v !== tag.value);
            } else if (tag.field === 'price_types') {
                this.selected_price_types = (this.selected_price_types || []).filter(v => v !== tag.value);
            } else if (tag.field === 'specification_values') {
                this.selected_specification_values = (this.selected_specification_values || []).filter(v => v !== tag.value);
            } else if (tag.field === 'characteristics' && tag.type_id) {
                const arr = (this.selected_characteristics?.[tag.type_id] || []).filter(v => v !== tag.value);
                this.selected_characteristics = {
                    ...this.selected_characteristics,
                    [tag.type_id]: arr,
                };
            }

            this.pagination.current_page = 1;
            this.updateSelectedFilters();
            this.applyFiltersDebounced();
        },

        resetFilter() {
            const keepTypeSlug = this.readTypeSlugFromPath();
            const keepSearch = this.search_query;

            this.loadings.catalog_loaded = false;
            this.loadings.filter_first_load = false;
            this.loadings.filter_limits_loaded = false;

            this.pagination.current_page = 1;

            this.products = [];
            this.filter_data = {
                limits: {
                    min_price: 0,
                    max_price: 0,
                    price: [0, 0],
                },
                types: [],
                categories: [],
                sub_categories: [],
                characteristics: [],
                price_types: [],
                specification_values: {},
            };

            this.filter = JSON.parse(JSON.stringify(defaultFilterData));

            this.selected_types = [];
            this.selected_categories = [];
            this.selected_sub_categories = [];
            this.selected_characteristics = {};
            this.selected_price_types = [];
            this.selected_specification_values = [];
            this.selected_filters = [];

            this.type_slug = keepTypeSlug || null;
            this.search_query = keepSearch || null;

            this.addParamsToUrl();
        },

        async initFromUrl() {
            this.resetFilter();
            this.syncUrlParamsWithState();
            await this.getFilterData();
            await this.getCatalog();

            this.is_filter_watcher_on = true;

            if (!this.popstateBound && checkDocument()) {
                window.addEventListener('popstate', () => {
                    this.syncUrlParamsWithState();
                    this.applyFiltersDebounced(0);
                });
                this.popstateBound = true;
            }
        },

        setGridMode(mode) {
            this.grid_mode = mode;
            setLocalStorage('grid_mode', mode, 43200);
        },
    },
});
